import { useState, useRef, useEffect } from 'react';
import { Search, User, X } from 'lucide-react';
import type { Contact, ContactUseCaseScore } from '../../../types';
import {
  getHighlightFromPriority,
  HIGHLIGHT_COLORS,
  PRIORITY_LABELS,
  BUYING_ROLE_LABELS,
} from '../../../lib/orgChartUtils';

interface Props {
  contacts: Contact[];
  ucScoreMap: Map<string, ContactUseCaseScore>;
  onSelectResult: (contact: Contact) => void;
}

function matchesQuery(contact: Contact, q: string): boolean {
  const lower = q.toLowerCase();
  const fullName = `${contact.first_name} ${contact.last_name}`.toLowerCase();
  if (fullName.includes(lower)) return true;
  if (contact.role?.toLowerCase().includes(lower)) return true;
  if (contact.department?.toLowerCase().includes(lower)) return true;
  if (contact.email?.toLowerCase().includes(lower)) return true;
  return false;
}

export default function ContactSearchBar({ contacts, ucScoreMap, onSelectResult }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = query.trim().length > 0
    ? contacts.filter(c => matchesQuery(c, query.trim())).slice(0, 12)
    : [];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = (contact: Contact) => {
    onSelectResult(contact);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-app-muted pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search contacts by name, role, department..."
          className="w-full pl-9 pr-8 py-2 text-sm bg-app-surface border border-app-line rounded-lg focus:outline-none focus:ring-2 focus:ring-forest-200 focus:border-forest-400 transition placeholder:text-app-muted"
        />
        {query && (
          <button
            onClick={() => { setQuery(''); setOpen(false); }}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 text-app-muted hover:text-app-muted rounded"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-app-surface border border-app-line rounded-lg shadow-none max-h-80 overflow-y-auto">
          {filtered.map(contact => {
            const score = ucScoreMap.get(contact.id);
            const highlight = score
              ? getHighlightFromPriority(score.outbound_priority as any)
              : 'low';
            const colors = HIGHLIGHT_COLORS[highlight];
            const priorityLabel = score
              ? PRIORITY_LABELS[score.outbound_priority as keyof typeof PRIORITY_LABELS] || ''
              : '';

            return (
              <button
                key={contact.id}
                onClick={() => handleSelect(contact)}
                className="w-full text-left px-3 py-2.5 hover:bg-app-canvas transition-colors flex items-center gap-3 border-b border-app-line last:border-b-0"
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${colors.bg}`}>
                  <User className={`w-4 h-4 ${colors.text}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-app-ink truncate">
                    {contact.first_name} {contact.last_name}
                  </p>
                  <p className="text-xs text-app-muted truncate">{contact.role}</p>
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  {contact.department && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-app-canvas rounded text-app-muted">
                      {contact.department}
                    </span>
                  )}
                  {priorityLabel && highlight !== 'low' && (
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                      {priorityLabel}
                    </span>
                  )}
                  {contact.buying_committee_role && (
                    <span className="text-[10px] text-app-muted">
                      {BUYING_ROLE_LABELS[contact.buying_committee_role]}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}

      {open && query.trim().length > 0 && filtered.length === 0 && (
        <div className="absolute z-50 mt-1 w-full bg-app-surface border border-app-line rounded-lg shadow-none p-4 text-center">
          <p className="text-sm text-app-muted">No contacts found</p>
        </div>
      )}
    </div>
  );
}
